// Statutory Holiday Pack governance (statutory-holiday program Phase 2).
// Same maker-checker lifecycle as country packs: draft → in_review → published
// → superseded; the author cannot publish their own pack; a change reason is
// mandatory to publish (same-day effective dates are allowed for gazette
// emergencies — D5). Publishing fans the entries out to every subscribed
// tenant's leave calendar via holiday_pack.published.
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);
const BASE = `${API_V1}/payroll/platform/holiday-packs`;

function authHeader() {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}
const H = () => ({ headers: { ...authHeader() } });

export type HolidayPackStatus = 'draft' | 'in_review' | 'published' | 'superseded';

export interface HolidayPackEntry {
  holiday_date: string;
  observed_date?: string | null;
  name: string;
  kind: 'fixed' | 'movable' | 'declared';
  half_day: boolean;
  notes?: string | null;
}

export interface HolidayPack {
  id: string;
  country_code: string;
  region?: string | null;
  version: number;
  status: HolidayPackStatus;
  name: string;
  source_authority?: string | null;
  notes?: string | null;
  change_reason?: string | null;
  effective_from?: string | null;
  effective_to?: string | null;
  published_at?: string | null;
  source: string; // seed | ui | import
  created_at: string;
  updated_at: string;
  entries?: HolidayPackEntry[];
}

export interface HolidayPackSubscription {
  tenant_id: string;
  country_code: string;
  locked: boolean;
  auto: boolean;
  created_at?: string | null;
}

export async function listHolidayPacks(): Promise<HolidayPack[]> {
  const { data } = await axios.get(BASE, H());
  return data.packs ?? [];
}

export async function getHolidayPack(id: string): Promise<HolidayPack> {
  const { data } = await axios.get(`${BASE}/${id}`, H());
  return data;
}

export async function createHolidayPackDraft(body: {
  country_code: string;
  region?: string;
  name: string;
  reason?: string;
  clone_from_active?: boolean;
  entries?: HolidayPackEntry[];
}): Promise<{ id: string }> {
  const { data } = await axios.post(BASE, body, H());
  return data;
}

export async function updateHolidayPackDraft(
  id: string,
  body: { name?: string; reason?: string; entries: HolidayPackEntry[] },
): Promise<void> {
  await axios.put(`${BASE}/${id}`, body, H());
}

export async function submitHolidayPack(id: string): Promise<void> {
  await axios.post(`${BASE}/${id}/submit`, {}, H());
}

export async function publishHolidayPack(id: string, effective_from: string, reason: string): Promise<void> {
  await axios.post(`${BASE}/${id}/publish`, { effective_from, reason }, H());
}

export async function rejectHolidayPack(id: string, reason?: string): Promise<void> {
  await axios.post(`${BASE}/${id}/reject`, { reason }, H());
}

export async function discardHolidayPack(id: string): Promise<void> {
  await axios.delete(`${BASE}/${id}`, H());
}

// Which tenants follow which statutory jurisdictions (leave-service via the
// gateway's platform proxy).
export async function listHolidayPackSubscriptions(countryCode?: string): Promise<HolidayPackSubscription[]> {
  const { data } = await axios.get(`${API_V1}/leave/platform/holiday-pack-subscriptions`, {
    ...H(),
    params: countryCode ? { country_code: countryCode } : undefined,
  });
  return data.subscriptions ?? [];
}
